"use client";

import * as React from 'react';

/**
 * What a FormField tells the control inside it: the id its label points at,
 * the ids of its description and error, and whether it is invalid. Input,
 * Textarea, Select, Checkbox and Switch read it, so a field's label, help and
 * error are associated with its control without wiring ids by hand.
 */
export interface FieldContextValue {
  id: string;
  describedBy?: string;
  invalid: boolean;
  required: boolean;
}

export const FieldContext = React.createContext<FieldContextValue | null>(null);

/**
 * The accessibility props a control takes from its field. Any it was given
 * directly win: a control that wires its own aria-describedby keeps exactly
 * that.
 */
export function useFieldControl<
  P extends {
    id?: string;
    'aria-describedby'?: string;
    'aria-invalid'?: React.AriaAttributes['aria-invalid'];
    required?: boolean;
  },
>(props: P, options: { required?: boolean } = {}): P {
  const field = React.useContext(FieldContext);
  if (!field) return props;
  const takesRequired = options.required ?? true;
  return {
    ...props,
    id: props.id ?? field.id,
    'aria-describedby': props['aria-describedby'] ?? field.describedBy,
    'aria-invalid': props['aria-invalid'] ?? (field.invalid || undefined),
    ...(takesRequired ? { required: props.required ?? (field.required || undefined) } : {}),
  };
}

/** Field outline, hover, focus, invalid and disabled, shared by every text control. */
export const fieldClassName = [
  'w-full rounded-md border border-input bg-surface text-sm text-foreground shadow-xs',
  'placeholder:text-placeholder',
  'transition-[border-color,box-shadow] duration-[var(--duration-fast)]',
  'hover:border-muted-foreground',
  'focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25',
  'aria-[invalid=true]:border-danger aria-[invalid=true]:focus-visible:ring-danger/25',
  'disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-disabled disabled:shadow-none',
  // A read-only text field reads as fixed; a select is never typed in, so it is not one.
  '[&:read-only:not(select)]:bg-subtle',
].join(' ');
